/**
 * La explicación del SUELO DE IMPORTACIÓN de SALES_NO_LOTS (#2219): tipos y
 * lector tolerante, escritos UNA vez porque el backend la congela y el
 * navegador la lee por dos caminos (la comprobación previa y el resumen del
 * informe ya generado). Un solo narrador evita que las dos pantallas y el
 * servidor discrepen sobre qué es una explicación «entera».
 */

/** Un medio afectado y la fecha desde la que su libro está completo. */
export interface PlatformImportFloor {
  platform: string
  /** ISO-8601 completo, el MISMO formato que Saldos entrega para esta fecha. */
  floorDate: string
  /**
   * El nombre del CATÁLOGO (`bat.exchanges.name`) para ese slug.
   *
   * Medido por la UI el 2026-09-14: la comprobación previa decía «el libro de
   * Hyperliquid sólo está completo desde…» mientras el catálogo, la rejilla y
   * Saldos dicen «HyperLiquid». El navegador sólo tenía el slug y lo
   * re-deletreaba, que no puede reproducir un nombre elegido por un
   * administrador. Opcional: un resumen congelado antes de este campo no lo trae
   * y la pantalla vuelve a derivarlo del slug.
   */
  platformDisplayName?: string
}

/**
 * SALES_NO_LOTS explicado por el SUELO DE IMPORTACIÓN de sus medios (#2219).
 *
 * Sólo viaja cuando el suelo explica a TODAS las ventas contadas — nunca a
 * algunas— y ésa es exactamente la condición con la que el backend degrada la
 * gravedad a WARNING. Su presencia es, por tanto, lo que decide QUÉ frase
 * redacta el panel: la del suelo («importa el historial anterior al {{date}}»)
 * o la genérica («importa primero las compras que faltan»). Aplicar una a la
 * causa de la otra manda al usuario a buscar donde no está.
 */
export interface ImportFloorExplanation {
  /** Ordenados por medio para que la frase no se reordene entre sondeos. */
  platformFloors: PlatformImportFloor[]
}

/** Una cadena con contenido real. Un medio o una fecha en blanco no se pueden nombrar. */
export function readNonEmptyString(source: Record<string, unknown>, field: string): string | null {
  const value = source[field]
  if (typeof value !== 'string' || value.trim() === '') return null
  return value
}

/**
 * La explicación del suelo de importación, si viene entera.
 *
 * Recibe la explicación EN CRUDO (no el sobre que la contiene).
 *
 * ⚠️ TODO O NADA. Si una sola entrada no trae medio y fecha, se descarta la
 * explicación COMPLETA: media lista encendería la frase «importa el historial
 * anterior al {{date}}» sin poder nombrar todos los medios a los que se
 * refiere, y esa frase manda a un sitio distinto que la genérica. Con `null`, el
 * panel redacta la genérica, que nunca miente aunque sea menos precisa.
 */
export function toImportFloorExplanation(raw: unknown): ImportFloorExplanation | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null

  const rawFloors = (raw as Record<string, unknown>)['platformFloors']
  if (!Array.isArray(rawFloors) || rawFloors.length === 0) return null

  const platformFloors: PlatformImportFloor[] = []
  for (const entry of rawFloors) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return null
    const fields = entry as Record<string, unknown>
    const platform = readNonEmptyString(fields, 'platform')
    const floorDate = readNonEmptyString(fields, 'floorDate')
    if (platform === null || floorDate === null) return null
    // Opcional: un resumen congelado antes de que el backend lo enviara no lo
    // trae, y eso no hace la explicación menos entera.
    const platformDisplayName = readNonEmptyString(fields, 'platformDisplayName')
    platformFloors.push({
      platform,
      floorDate,
      ...(platformDisplayName === null ? {} : { platformDisplayName }),
    })
  }

  return { platformFloors }
}
